
import React, { useState } from "react";
import "./DateSelector.scss";
import { ImCross } from "react-icons/im";
import { 
  FiCalendar, 
  FiUsers, 
  FiDollarSign, 
  FiCheck, 
  FiChevronRight,
  FiClock,
  FiMapPin,
  FiStar,
  FiInfo
} from "react-icons/fi";

const DateSelector = ({ 
  dates, 
  tripTitle, 
  onClose, 
  onSelectDate, 
  setSelectedDate, 
  selectedDate, 
  formData, 
  setFormData 
}) => {
  const availableDates = JSON.parse(dates);
  const [filter, setFilter] = useState("all"); // all, available, limited, soldout

  // Calculate group discounts
  const calculateGroupDiscount = (price, people = 4) => {
    if (people >= 6) return price - 60;
    if (people >= 4) return price - 50;
    return price;
  };

  // Filter dates based on availability
  const filteredDates = availableDates.filter(date => {
    if (filter === "available") return date.seatsAvailable > 5;
    if (filter === "limited") return date.seatsAvailable > 0 && date.seatsAvailable <= 5;
    if (filter === "soldout") return date.seatsAvailable === 0;
    return true;
  });

  // Get availability status
  const getAvailabilityStatus = (seats) => {
    if (seats <= 0) return { 
      text: "Sold Out", 
      color: "#ef4444", 
      bg: "#fee2e2",
      icon: "❌" 
    };
    if (seats <= 5) return { 
      text: "Limited", 
      color: "#f59e0b", 
      bg: "#fef3c7",
      icon: "⚡" 
    };
    return { 
      text: "Available", 
      color: "#10b981", 
      bg: "#d1fae5",
      icon: "✅" 
    };
  };

  return (
    <div className="date-modal-overlay" onClick={onClose}>
      <div className="date-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <div className="header-content">
            <div className="header-icon">
              <FiCalendar />
            </div>
            <div>
              <h2>Select Your Tour Date</h2>
              <p className="trip-title">{tripTitle}</p>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            <ImCross />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat">
            <FiCalendar />
            <span>{availableDates.length} Dates Available</span>
          </div>
          <div className="stat">
            <FiUsers />
            <span>Group Discounts Available</span>
          </div>
          <div className="stat">
            <FiCheck />
            <span>Flexible Booking</span>
          </div>
        </div>

        {/* Filters */}
        <div className="filters">
          <button 
            className={`filter-btn ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All Dates
          </button>
          <button 
            className={`filter-btn ${filter === "available" ? "active" : ""}`}
            onClick={() => setFilter("available")}
          >
            Available
          </button>
          <button 
            className={`filter-btn ${filter === "limited" ? "active" : ""}`}
            onClick={() => setFilter("limited")}
          >
            Limited
          </button>
          <button 
            className={`filter-btn ${filter === "soldout" ? "active" : ""}`}
            onClick={() => setFilter("soldout")}
          >
            Sold Out
          </button>
        </div>

        {/* Dates Table */}
        <div className="dates-container">
          {filteredDates.length === 0 ? (
            <div className="empty-state">
              <FiCalendar size={48} />
              <h3>No dates match your filter</h3>
              <p>Try selecting a different filter option</p>
              <button 
                className="reset-filter"
                onClick={() => setFilter("all")}
              >
                Show All Dates
              </button>
            </div>
          ) : (
            <>
              <div className="date-header">
                <div className="header-col date-col">Date & Duration</div>
                <div className="header-col seats-col">Availability</div>
                <div className="header-col price-col">Price & Discount</div>
              </div>

              <div className="dates-list">
                {filteredDates.map((date, index) => {
                  const availability = getAvailabilityStatus(date.seatsAvailable);
                  const groupPrice = calculateGroupDiscount(date.price);
                  const discount = date.price - groupPrice;
                  
                  return (
                    <div
                      key={index}
                      className={`date-card ${
                        selectedDate?.dateRange === date.dateRange ? "active" : ""
                      } ${date.seatsAvailable === 0 ? "disabled" : ""}`}
                      onClick={() => {
                        if (date.seatsAvailable > 0) {
                          setSelectedDate(date);
                          setFormData({
                            ...formData,
                            date: date.dateRange,
                            amount: date.price
                          });
                        }
                      }}
                    >
                      {/* Date Info */}
                      <div className="date-col">
                        <div className="date-main">
                          <FiCalendar className="date-icon" />
                          <div>
                            <h4 className="date-range">{date.dateRange}</h4>
                            <div className="date-details">
                              <span className="duration">
                                <FiClock /> {date.duration || "10 Days"}
                              </span>
                              {date.departurePoint && (
                                <span className="departure">
                                  <FiMapPin /> {date.departurePoint}
                                </span>
                              )}
                              {date.difficulty && (
                                <span className="difficulty">
                                  <FiStar /> {date.difficulty}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Availability */}
                      <div className="seats-col">
                        <div className="availability-badge" style={{
                          color: availability.color,
                          backgroundColor: availability.bg
                        }}>
                          <span className="availability-icon">{availability.icon}</span>
                          <div>
                            <strong>{availability.text}</strong>
                            {date.seatsAvailable > 0 && (
                              <div className="seats-count">
                                {date.seatsAvailable} seats left
                              </div>
                            )}
                          </div>
                        </div>
                        {date.seatsAvailable > 0 && date.seatsAvailable <= 5 && (
                          <div className="urgency-badge">
                            <FiInfo /> Book soon!
                          </div>
                        )}
                      </div>

                      {/* Pricing */}
                      <div className="price-col">
                        <div className="price-info">
                          <div className="original-price">
                            <span className="price-label">From</span>
                            <span className="price-amount">${date.price}</span>
                            <span className="price-per">per person</span>
                          </div>
                          
                          {discount > 0 && (
                            <div className="group-discount">
                              <FiUsers className="discount-icon" />
                              <div className="discount-info">
                                <span className="discount-label">Group of 4:</span>
                                <span className="discount-price">${groupPrice}</span>
                                <span className="discount-save">Save ${discount}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Select Indicator */}
                      <div className="select-indicator">
                        {selectedDate?.dateRange === date.dateRange ? (
                          <div className="selected-check">
                            <FiCheck />
                          </div>
                        ) : date.seatsAvailable > 0 ? (
                          <div className="select-arrow">
                            <FiChevronRight />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="footer-info">
            {selectedDate ? (
              <div className="selected-info">
                <div className="selected-date">
                  <strong>Selected: </strong>
                  {selectedDate.dateRange}
                </div>
                <div className="selected-price">
                  ${selectedDate.price} per person
                </div>
              </div>
            ) : (
              <div className="selection-hint">
                <FiInfo /> Select a date to continue
              </div>
            )}
          </div>
          
          <div className="footer-actions">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="confirm-btn"
              disabled={!selectedDate}
              onClick={() => onSelectDate(selectedDate)}
            >
              {selectedDate ? (
                <>
                  Continue to Booking
                  <FiChevronRight />
                </>
              ) : (
                "Select Date to Continue"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateSelector;